import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['minContainer', 'maxContainer', 'modeContainer', 'mitigationContainer', 'titleRequired', 'monteCarloDetails', 'outputFields', 'percentageOutputs']
  static values = {
    zeroAmount: { type: String, default: '0' },
    zeroPercentage: { type: String, default: '0.0%' }
  }

  declare minContainerTargets: HTMLElement[]
  declare maxContainerTargets: HTMLElement[]
  declare modeContainerTargets: HTMLElement[]
  declare mitigationContainerTargets: HTMLElement[]
  declare titleRequiredTargets: HTMLElement[]
  declare monteCarloDetailsTargets: HTMLElement[]
  declare outputFieldsTargets: HTMLElement[]
  declare percentageOutputsTargets: HTMLElement[]
  declare zeroAmountValue: string
  declare zeroPercentageValue: string

  private previousCalculationType: string | null = null
  private resultsObserver: MutationObserver | null = null

  connect (): void {
    this.previousCalculationType = this.getCurrentCalculationType()
    this.toggleFields()
    this.observeResults()
  }

  disconnect (): void {
    this.resultsObserver?.disconnect()
  }

  toggleFields (): void {
    const currentType = this.getCurrentCalculationType()
    if (currentType == null) return

    const isDeterministic = currentType === 'deterministic'
    const hasChanged = this.previousCalculationType !== null && this.previousCalculationType !== currentType

    if (hasChanged) {
      this.clearAllErrors()
      this.resetOutputFields()
      const allContainers = [
        ...this.minContainerTargets,
        ...this.maxContainerTargets,
        ...this.modeContainerTargets,
        ...this.mitigationContainerTargets
      ]
      allContainers.forEach((container) => {
        this.clearValuesInContainer(container)
      })
    }

    this.previousCalculationType = currentType
    this.updateFieldVisibility(isDeterministic)
  }

  sync (): void {
    setTimeout(() => {
      this.previousCalculationType = this.getCurrentCalculationType()
      this.updateFieldVisibility(this.previousCalculationType === 'deterministic')
    }, 0)
  }

  private observeResults (): void {
    const results = this.element.querySelector('[data-fair-results]') ?? this.element.querySelector('#fair-results')
    const parent = results?.parentElement
    if (parent == null) return

    this.resultsObserver = new MutationObserver(() => {
      this.toggleFields()
    })
    this.resultsObserver.observe(parent, { childList: true, subtree: true })
  }

  private resetOutputFields (): void {
    this.outputFieldsTargets.forEach((el) => {
      el.textContent = this.zeroAmountValue
    })
    this.percentageOutputsTargets.forEach((el) => {
      el.textContent = this.zeroPercentageValue
    })
  }

  private getCurrentCalculationType (): string | null {
    const radio = this.element.querySelector<HTMLInputElement>(
      'input[type="radio"][name$="[calculation_type]"]:checked'
    )
    return radio?.value ?? null
  }

  private updateFieldVisibility (isDeterministic: boolean): void {
    const showMinMax = !isDeterministic

    this.setDisplay(this.minContainerTargets, showMinMax)
    this.setDisplay(this.maxContainerTargets, showMinMax)
    this.setDisplayInline(this.titleRequiredTargets, isDeterministic)

    this.modeContainerTargets.forEach((container) => {
      const label = container.querySelector('label')
      if (label != null) {
        label.style.setProperty('display', isDeterministic ? 'none' : 'block', 'important')
      }
      container.style.display = 'block'
    })

    this.setDisplay(this.monteCarloDetailsTargets, !isDeterministic)
  }

  private clearAllErrors (): void {
    const allContainers = [
      ...this.minContainerTargets,
      ...this.maxContainerTargets,
      ...this.modeContainerTargets,
      ...this.mitigationContainerTargets
    ]

    allContainers.forEach((container) => {
      this.clearErrorsInContainer(container)
    })
  }

  private clearErrorsInContainer (container: HTMLElement): void {
    container.querySelectorAll('[data-controller*="form-reset-invalid"]').forEach((element) => {
      this.resetFormInvalidController(element as HTMLElement)
    })

    container.querySelectorAll('.error-message').forEach((message) => {
      message.remove()
    })
  }

  private resetFormInvalidController (element: HTMLElement): void {
    const controller = this.application.getControllerForElementAndIdentifier(
      element,
      'form-reset-invalid'
    )

    if ((controller != null) && typeof (controller as any).reset === 'function') {
      (controller as any).reset()
    }
  }

  private setDisplay (elements: HTMLElement[], show: boolean): void {
    elements.forEach((el) => {
      el.style.display = show ? 'block' : 'none'
    })
  }

  private setDisplayInline (elements: HTMLElement[], show: boolean): void {
    elements.forEach((el) => {
      if (show) {
        el.classList.add('inline')
        el.style.display = 'inline'
      } else {
        el.classList.remove('inline')
        el.style.display = 'none'
      }
    })
  }

  private clearValuesInContainer (container: HTMLElement): void {
    container.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
      'input, select, textarea'
    ).forEach((field) => {
      if (field instanceof HTMLInputElement) {
        if (field.type === 'checkbox' || field.type === 'radio') {
          field.checked = false
        } else {
          field.value = ''
        }
      } else {
        field.value = ''
      }
    })
  }
}
